import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react'
import { useAppStore } from '@core/store'
import { useAuth } from '@core/auth/useAuth'
import { sessoesApi } from '@core/sessoes/sessoesApi'
import { sessoesMudaram, useVersaoDasSessoes } from '@core/sessoes/sessaoPartilhadaEstado'
import { modelosApi } from './modelosApi'
import { recarregarModelos, type EstadoModelos } from './modelosPartilhados'
import { itensDaReciclagem, textoApagadoDeVez, type ItemDaReciclagem } from './reciclagemRegras'

function mensagem(err: unknown): string {
  return err instanceof Error ? err.message : 'Não foi possível falar com o servidor.'
}

/** "Reciclagem", na barra lateral, por baixo das sessões partilhadas: as
 *  revisões e as sessões que a pessoa apagou (ou, para um administrador
 *  VN2R, que alguém apagou) há menos de 7 dias, com o dia em que o
 *  servidor as apaga de vez, e "Restaurar". Fechada por omissão, e
 *  escondida quando está vazia. */
export default function Reciclagem({ estado }: { estado: EstadoModelos }) {
  const { workspace } = useAuth()
  const workspaceId = workspace?.id ?? null
  const pushToast = useAppStore((s) => s.pushToast)
  const versaoSessoes = useVersaoDasSessoes()
  const [aberto, setAberto] = useState(false)
  const [itens, setItens] = useState<ItemDaReciclagem[]>([])
  const [aRestaurar, setARestaurar] = useState<string | null>(null)
  const [recarregar, setRecarregar] = useState(0)
  const ativo = Boolean(estado.estado?.ativo)

  // Volta a ler quando a lista dos modelos muda (apagar, restaurar, uma
  // revisão nova), não a cada refrescamento periódico dela.
  const assinaturaDosModelos = useMemo(
    () => (estado.lista ?? []).map((m) => m.id).join(','),
    [estado.lista],
  )

  useEffect(() => {
    if (!workspaceId || !ativo) return
    let vivo = true
    void Promise.all([
      modelosApi.reciclagem().catch(() => []),
      sessoesApi.reciclagem().catch(() => []),
    ]).then(([modelos, sessoes]) => {
      if (vivo) setItens(itensDaReciclagem(modelos, sessoes))
    })
    return () => {
      vivo = false
    }
  }, [workspaceId, ativo, assinaturaDosModelos, versaoSessoes, recarregar])

  if (!workspaceId || !ativo || itens.length === 0) return null

  const restaurar = async (item: ItemDaReciclagem) => {
    setARestaurar(item.id)
    try {
      if (item.tipo === 'modelo') await modelosApi.restaurar(item.id)
      else await sessoesApi.restaurar(item.id)
      pushToast(`Restaurado da Reciclagem: ${item.titulo}.`, { type: 'success' })
    } catch (err) {
      pushToast(`Não foi possível restaurar: ${mensagem(err)}`, { type: 'error', timeout: 0 })
    } finally {
      setARestaurar(null)
      setRecarregar((n) => n + 1)
      void recarregarModelos()
      sessoesMudaram()
    }
  }

  return (
    <div className="border-b border-[var(--color-border)]" data-campo="reciclagem">
      <div className="px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
        <button
          type="button"
          onClick={() => setAberto((v) => !v)}
          className="flex items-center gap-1 uppercase"
          aria-expanded={aberto}
          title="O que foi apagado do servidor nos últimos 7 dias e ainda se pode restaurar"
        >
          {aberto ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          Reciclagem ({itens.length})
        </button>
      </div>
      {aberto && (
        <ul className="flex max-h-56 flex-col gap-1.5 overflow-y-auto px-3 pb-2 text-xs">
          {itens.map((item) => (
            <li
              key={`${item.tipo}:${item.id}`}
              className="flex items-center gap-1 rounded border border-[var(--color-border-subtle)] px-1.5 py-1"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-[var(--color-text)]" title={item.titulo}>
                  {item.titulo}
                </div>
                <div className="truncate text-[10px] text-[var(--color-text-dim)]">
                  {textoApagadoDeVez(item.expiraEm, item.tipo === 'sessao')}
                </div>
              </div>
              <button
                type="button"
                disabled={aRestaurar === item.id}
                onClick={() => void restaurar(item)}
                title="Restaurar: volta a aparecer e a contar para o espaço do workspace"
                aria-label={`Restaurar ${item.titulo}`}
                className="flex shrink-0 items-center gap-0.5 rounded border border-[var(--color-border)] px-1.5 py-0 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
              >
                <RotateCcw size={10} aria-hidden />
                Restaurar
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
